import { CompanyLocationsListPreviewOutDTO } from '@api/ringil-api.v2';

/**
 * Ramps are manually orderable per location (`orderIndex`, set on the locality detail) and the location
 * list returns them in that order. The opening-hours endpoint the calendar loads its columns from carries
 * no `orderIndex` and no guaranteed order, so the ordering is taken from the location list here and
 * applied to the calendar resources.
 */

/** rampId → its rank across all locations, following the location order and each location's BE ramp order. */
export type RampOrderLookup = Map<string, number>;

export const buildRampOrderLookup = (locations: CompanyLocationsListPreviewOutDTO[]): RampOrderLookup => {
  const lookup: RampOrderLookup = new Map();
  let rank = 0;
  (locations ?? []).forEach(location => {
    (location?.ramps ?? []).forEach(ramp => {
      if (!lookup.has(ramp.rampId)) lookup.set(ramp.rampId, rank++);
    });
  });
  return lookup;
};

/** Ramps missing from the lookup (location list not loaded yet, foreign ramp) stay last, ordered by name. */
export const sortByRampOrder = <T extends { rampId: string; name?: string }>(items: T[], lookup: RampOrderLookup): T[] =>
  [...(items ?? [])].sort((a, b) => {
    const ar = lookup.get(a.rampId) ?? Number.MAX_SAFE_INTEGER;
    const br = lookup.get(b.rampId) ?? Number.MAX_SAFE_INTEGER;
    if (ar !== br) return ar - br;
    return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
  });
